import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import Link from '@mui/material/Link';
import { useFormik } from 'formik';
import { ReadonlyURLSearchParams, useSearchParams } from 'next/navigation';
import React from 'react';
import * as yup from 'yup';
import { EngagementEvents, logKariusEvent } from '../../Analytics';
import { LoginCredentials, LoginFormProps } from '../../auth/authTypes';
import { privacyPolicy, termsOfUse } from '../../Config/links';
import { PasswordResetModal } from '../../Modal/ResetPassword';
import Colors from '../../theme/colors';
import getTestId from '../Components/GetTestId';

export default function LoginForm({ onSubmit, loading = false }: LoginFormProps) {
  const [showPassword, setShowPassword] = React.useState(false);

  const [showPasswordResetModal, setShowPasswordResetModal] = React.useState(false);
  const [showUnblockSuccessAlert, setShowUnblockSuccessAlert] = React.useState(false);
  const [modalFromUrlShowed, setModalFromUrlShowed] = React.useState(false);

  const searchParams = useSearchParams();

  if (checkIsRedirectFromAuth0ExpiredPage(searchParams) && !modalFromUrlShowed) {
    setShowPasswordResetModal(true);
    setModalFromUrlShowed(true);
  }

  if (checkIsRedirectFromSuccessUnblock(searchParams) && !modalFromUrlShowed) {
    setShowUnblockSuccessAlert(true);
    setModalFromUrlShowed(true);
    logKariusEvent(EngagementEvents.reset_password_or_unlock_successful, {
      type: 'locked_account',
    });
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const validationSchema = yup.object({
    email: yup.string().trim().required('An email or username is required for sign in'),
    password: yup.string().trim().required('A password is required for sign in'),
  });

  const { errors, touched, values, handleChange, handleSubmit, setErrors } = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values: LoginCredentials) => {
      onSubmit(values, showLoginError);
    },
  });

  const showLoginError = (message: any) => {
    if (message) {
      setErrors({ password: message, email: ' ' });
      return;
    }
  };

  return (
    <>
      <Grid container component='form' onSubmit={handleSubmit} spacing={3}>
        {showUnblockSuccessAlert && (
          <Grid item xs={12}>
            <Alert
              sx={{
                fontSize: '14px',
                backgroundColor: Colors.supportSuccessBackground,
                borderRadius: '3.5px',
                color: Colors.supportSuccess,
                border: '2px',
                borderStyle: 'solid',
                borderColor: Colors.supportSuccessBorder,
                borderLeftWidth: '4px',
                borderLeftColor: Colors.supportSuccess,
                height: '50px',
                justifyContent: 'left',
                alignItems: 'center',
                '& .MuiAlert-message': {
                  marginTop: '0px',
                },
                '& .MuiAlert-icon': {
                  marginTop: '0px',
                },
              }}
              iconMapping={{
                success: <CheckCircleIcon fontSize='inherit' />,
              }}
            >
              Your account has been unlocked. Please sign in.
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            fullWidth
            id='email'
            name='email'
            label='Email or username'
            value={values.email}
            onChange={handleChange}
            error={touched.email && !!errors.email}
            helperText={(touched.email && errors.email) || ' '}
            inputProps={{
              'data-test': getTestId('email'),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id='password'
            name='password'
            label='Password'
            value={values.password}
            onChange={handleChange}
            error={touched.password && !!errors.password}
            helperText={(touched.password && errors.password) || ' '}
            type={showPassword ? 'text' : 'password'}
            inputProps={{
              'data-test': getTestId('password'),
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    size='large'
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            color='primary'
            variant='contained'
            fullWidth
            type='submit'
            sx={{
              borderRadius: '20px',
              marginBottom: '5px',
            }}
            data-test={getTestId('sign-in')}
          >
            {loading ? <CircularProgress size={25} sx={{ color: Colors.white }} /> : 'Sign In'}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Typography align='center' variant='body2'>
            <Link
              sx={{
                fontSize: '14px',
                fontWeight: '500',
                color: Colors.teal,
                cursor: 'pointer',
              }}
              onClick={(event) => {
                setShowPasswordResetModal(true);
                logKariusEvent(EngagementEvents.reset_password_tapped);
                event.preventDefault();
              }}
            >
              Reset password
            </Link>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography
            align='center'
            variant='body2'
            sx={{
              fontSize: '14px',
            }}
          >
            By signing in, you agree to the
            <br />{' '}
            <a
              href={termsOfUse}
              className='link-text'
              target='_blank'
              rel='noreferrer'
              onClick={() => logKariusEvent(EngagementEvents.tou_tapped)}
            >
              Terms of Use
            </a>{' '}
            and{' '}
            <a
              href={privacyPolicy}
              className='link-text'
              target='_blank'
              rel='noreferrer'
              onClick={() => logKariusEvent(EngagementEvents.privacy_tapped)}
            >
              Privacy Policy
            </a>
          </Typography>
        </Grid>
      </Grid>
      <PasswordResetModal
        open={showPasswordResetModal}
        resetEmail={searchParams.get('email')}
        isResetEmailExpired={checkIsRedirectFromAuth0ExpiredPage(searchParams)}
        handleClose={() => setShowPasswordResetModal(false)}
      />
    </>
  );
}

function checkIsRedirectFromAuth0ExpiredPage(searchParams: ReadonlyURLSearchParams) {
  return (
    searchParams.get('success') === 'false' &&
    searchParams.get('message') === 'Access expired.' &&
    !searchParams.get('redirectFromBlockEmail')
  );
}
function checkIsRedirectFromSuccessUnblock(searchParams: ReadonlyURLSearchParams) {
  return searchParams.get('success') === 'true' && searchParams.get('redirectFromBlockEmail');
}
